<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { collection, addDoc, serverTimestamp, query, where, orderBy, getDocs } from '@/lib/firestore'
import { db } from '@/config/firebase'
import { useAuthStore } from '@/stores/auth'
import { useRoute } from 'vue-router'
import { useToast } from '@/components/ui/toast'
import { formatDate, formatDateTime } from '@/utils/formatters'
import type { FeedbackType, UserFeedback } from '@/types/help'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Badge } from '@/components/ui/badge'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs'
import {
  MessageSquare,
  Bug,
  Lightbulb,
  TrendingUp,
  HelpCircle,
  Loader2,
  CheckCircle,
  Star,
  Clock,
  Send,
  Inbox,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Eye,
} from 'lucide-vue-next'

const authStore = useAuthStore()
const route = useRoute()
const { toast } = useToast()

// Form state
const feedbackType = ref<FeedbackType>('improvement')
const subject = ref('')
const description = ref('')
const rating = ref(0)
const hoveredStar = ref(0)
const isSubmitting = ref(false)
const isSubmitted = ref(false)
const submittedAsRating = ref(false)

// History state
const myFeedbacks = ref<UserFeedback[]>([])
const isLoadingHistory = ref(false)
const expandedFeedbackId = ref<string | null>(null)
const activeTab = ref('send')

const isRatingType = computed(() => feedbackType.value === 'rating')

const feedbackTypes = [
  { value: 'rating' as FeedbackType, label: 'Valoración', icon: Star, description: 'Califica tu experiencia' },
  { value: 'bug' as FeedbackType, label: 'Reportar Bug', icon: Bug, description: 'Algo no funciona correctamente' },
  { value: 'feature' as FeedbackType, label: 'Nueva Función', icon: Lightbulb, description: 'Sugerir una nueva funcionalidad' },
  { value: 'improvement' as FeedbackType, label: 'Mejora', icon: TrendingUp, description: 'Mejorar algo existente' },
  { value: 'question' as FeedbackType, label: 'Pregunta', icon: HelpCircle, description: 'Tengo una duda' },
]

const typeConfig: Record<FeedbackType, { icon: any; label: string; color: string }> = {
  bug: { icon: Bug, label: 'Bug', color: 'text-red-500 bg-red-500/10' },
  feature: { icon: Lightbulb, label: 'Feature', color: 'text-amber-500 bg-amber-500/10' },
  improvement: { icon: TrendingUp, label: 'Mejora', color: 'text-blue-500 bg-blue-500/10' },
  question: { icon: HelpCircle, label: 'Pregunta', color: 'text-violet-500 bg-violet-500/10' },
  rating: { icon: Star, label: 'Rating', color: 'text-yellow-500 bg-yellow-500/10' },
}

const statusConfig: Record<string, { label: string; variant: 'default' | 'secondary' | 'outline' | 'destructive' }> = {
  new: { label: 'Enviado', variant: 'default' },
  reviewed: { label: 'Revisado', variant: 'secondary' },
  in_progress: { label: 'En progreso', variant: 'outline' },
  resolved: { label: 'Resuelto', variant: 'secondary' },
  wont_fix: { label: 'Cerrado', variant: 'destructive' },
}

const pendingCount = computed(() =>
  myFeedbacks.value.filter((f) => f.status === 'new' || f.status === 'in_progress').length
)

const withResponseCount = computed(() =>
  myFeedbacks.value.filter((f) => f.adminResponse || (f.responses && f.responses.length > 0)).length
)

// Fetch user's feedback history
const fetchMyFeedbacks = async () => {
  if (!authStore.userId) return
  isLoadingHistory.value = true
  try {
    const q = query(
      collection(db, 'userFeedback'),
      where('userId', '==', authStore.userId),
      orderBy('createdAt', 'desc')
    )
    const snapshot = await getDocs(q)
    myFeedbacks.value = snapshot.docs.map((d) => {
      const data = d.data()
      return {
        id: d.id,
        ...data,
        createdAt: data.createdAt?.toDate?.() || new Date(),
        updatedAt: data.updatedAt?.toDate?.() || new Date(),
        responses: (data.responses || []).map((r: any) => ({
          ...r,
          createdAt: r.createdAt?.toDate?.() || new Date(),
        })),
      } as UserFeedback
    })
  } catch (error) {
    console.error('Error fetching feedback history:', error)
  } finally {
    isLoadingHistory.value = false
  }
}

const handleSubmit = async () => {
  if (isRatingType.value) {
    if (rating.value === 0) {
      toast({
        title: 'Valoración requerida',
        description: 'Por favor selecciona una calificación de 1 a 5 estrellas',
        variant: 'destructive',
      })
      return
    }
  } else {
    if (!subject.value.trim() || !description.value.trim()) {
      toast({
        title: 'Campos requeridos',
        description: 'Por favor completa el asunto y la descripción',
        variant: 'destructive',
      })
      return
    }
  }

  isSubmitting.value = true

  try {
    const feedbackData: Record<string, unknown> = {
      tenantId: authStore.userTenantId || null,
      userId: authStore.userId || 'anonymous',
      userEmail: authStore.userEmail || 'anonymous',
      userRole: authStore.userRole || 'unknown',
      type: feedbackType.value,
      currentPage: route.fullPath,
      status: 'new',
      responses: [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }

    if (isRatingType.value) {
      feedbackData.rating = rating.value
      feedbackData.subject = `Valoración: ${rating.value} estrellas`
      feedbackData.description = description.value.trim() || 'Sin comentarios adicionales'
    } else {
      feedbackData.subject = subject.value.trim()
      feedbackData.description = description.value.trim()
    }

    await addDoc(collection(db, 'userFeedback'), feedbackData)

    submittedAsRating.value = isRatingType.value
    isSubmitted.value = true
    toast({
      title: isRatingType.value ? 'Valoración enviada' : 'Feedback enviado',
      description: isRatingType.value
        ? 'Gracias por tu valoración. Tu opinión nos ayuda a mejorar.'
        : 'Gracias por tu comentario. Lo revisaremos pronto.',
    })

    // Refresh history
    fetchMyFeedbacks()
  } catch (error) {
    console.error('Error submitting feedback:', error)
    toast({
      title: 'Error',
      description: 'No se pudo enviar el feedback. Intenta de nuevo.',
      variant: 'destructive',
    })
  } finally {
    isSubmitting.value = false
  }
}

const resetForm = () => {
  isSubmitted.value = false
  submittedAsRating.value = false
  feedbackType.value = 'improvement'
  subject.value = ''
  description.value = ''
  rating.value = 0
  hoveredStar.value = 0
}

const toggleExpand = (id: string) => {
  expandedFeedbackId.value = expandedFeedbackId.value === id ? null : id
}

const getRatingText = (stars: number): string => {
  const texts = ['', 'Muy malo', 'Malo', 'Regular', 'Bueno', 'Excelente']
  return texts[stars] || ''
}

const renderStars = (n: number) => '★'.repeat(n) + '☆'.repeat(5 - n)

onMounted(fetchMyFeedbacks)
</script>

<template>
  <div>
    <div class="flex items-center gap-2 mb-4">
      <MessageSquare class="h-6 w-6 text-primary" />
      <h2 class="text-2xl font-bold">Feedback</h2>
    </div>

    <Tabs v-model="activeTab" class="w-full">
      <TabsList class="w-full mb-4">
        <TabsTrigger value="send" class="flex-1 gap-1.5">
          <Send class="h-3.5 w-3.5" />
          Enviar
        </TabsTrigger>
        <TabsTrigger value="history" class="flex-1 gap-1.5">
          <Inbox class="h-3.5 w-3.5" />
          Mis envíos
          <Badge
            v-if="myFeedbacks.length > 0"
            variant="secondary"
            class="text-[10px] px-1.5 py-0 ml-0.5"
          >
            {{ myFeedbacks.length }}
          </Badge>
        </TabsTrigger>
      </TabsList>

      <!-- ==================== SEND TAB ==================== -->
      <TabsContent value="send">
        <p class="text-muted-foreground text-sm mb-6">
          Tu opinión nos ayuda a mejorar. Cuéntanos qué podemos hacer mejor.
        </p>

        <!-- Success State -->
        <div v-if="isSubmitted" class="text-center py-10">
          <div class="h-14 w-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
            <CheckCircle class="h-7 w-7 text-green-500" />
          </div>
          <h3 class="text-lg font-semibold mb-2">
            {{ submittedAsRating ? '¡Gracias por tu valoración!' : '¡Feedback enviado!' }}
          </h3>
          <p class="text-muted-foreground text-sm mb-6">
            {{ submittedAsRating
              ? 'Tu opinión nos ayuda a mejorar continuamente.'
              : 'Lo revisaremos pronto. Puedes ver el estado en "Mis envíos".'
            }}
          </p>
          <div class="flex items-center justify-center gap-3">
            <Button variant="outline" size="sm" @click="resetForm">
              Enviar otro
            </Button>
            <Button variant="ghost" size="sm" @click="activeTab = 'history'">
              <Eye class="mr-2 h-3.5 w-3.5" />
              Ver mis envíos
            </Button>
          </div>
        </div>

        <!-- Form -->
        <form v-else class="space-y-5" @submit.prevent="handleSubmit">
          <!-- Feedback Type -->
          <div class="space-y-3">
            <Label>Tipo de feedback</Label>
            <RadioGroup v-model="feedbackType" class="grid grid-cols-2 gap-2.5">
              <div
                v-for="type in feedbackTypes"
                :key="type.value"
                class="relative"
              >
                <RadioGroupItem
                  :value="type.value"
                  :id="type.value"
                  class="peer sr-only"
                />
                <Label
                  :for="type.value"
                  class="flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted peer-data-[state=checked]:border-primary peer-data-[state=checked]:bg-primary/5 transition-colors"
                >
                  <component :is="type.icon" class="h-5 w-5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <p class="font-medium text-sm">{{ type.label }}</p>
                    <p class="text-xs text-muted-foreground">{{ type.description }}</p>
                  </div>
                </Label>
              </div>
            </RadioGroup>
          </div>

          <!-- Star Rating -->
          <div v-if="isRatingType" class="space-y-3">
            <Label>¿Cómo calificarías tu experiencia?</Label>
            <div class="flex flex-col items-center gap-3 py-3">
              <div class="flex gap-1">
                <button
                  v-for="star in 5"
                  :key="star"
                  type="button"
                  class="p-1 transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                  @click="rating = star"
                  @mouseenter="hoveredStar = star"
                  @mouseleave="hoveredStar = 0"
                >
                  <Star
                    class="h-8 w-8 transition-colors"
                    :class="[
                      (hoveredStar || rating) >= star
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-muted-foreground/30'
                    ]"
                  />
                </button>
              </div>
              <p
                class="text-sm font-medium transition-opacity"
                :class="hoveredStar || rating ? 'opacity-100' : 'opacity-0'"
              >
                {{ getRatingText(hoveredStar || rating) }}
              </p>
            </div>
            <div class="space-y-2">
              <Label for="rating-comment">Comentario (opcional)</Label>
              <Textarea
                id="rating-comment"
                v-model="description"
                placeholder="¿Quieres agregar algún comentario sobre tu experiencia?"
                rows="3"
                :disabled="isSubmitting"
              />
            </div>
          </div>

          <!-- Subject -->
          <div v-if="!isRatingType" class="space-y-2">
            <Label for="subject">Asunto</Label>
            <Input
              id="subject"
              v-model="subject"
              placeholder="Resumen breve de tu feedback"
              :disabled="isSubmitting"
            />
          </div>

          <!-- Description -->
          <div v-if="!isRatingType" class="space-y-2">
            <Label for="description">Descripción</Label>
            <Textarea
              id="description"
              v-model="description"
              placeholder="Describe con detalle tu sugerencia, problema o pregunta..."
              rows="4"
              :disabled="isSubmitting"
            />
          </div>

          <Button
            type="submit"
            class="w-full"
            :disabled="isSubmitting || (isRatingType ? rating === 0 : !subject.trim() || !description.trim())"
          >
            <Loader2 v-if="isSubmitting" class="h-4 w-4 mr-2 animate-spin" />
            <Send v-else class="h-4 w-4 mr-2" />
            Enviar Feedback
          </Button>
        </form>
      </TabsContent>

      <!-- ==================== HISTORY TAB ==================== -->
      <TabsContent value="history">
        <!-- Loading -->
        <div v-if="isLoadingHistory" class="space-y-3 py-4">
          <Skeleton v-for="i in 3" :key="i" class="h-20 w-full" />
        </div>

        <!-- Empty -->
        <div v-else-if="myFeedbacks.length === 0" class="text-center py-12">
          <Inbox class="h-10 w-10 text-muted-foreground mx-auto mb-3" />
          <p class="text-sm text-muted-foreground mb-4">Aún no has enviado feedback.</p>
          <Button variant="outline" size="sm" @click="activeTab = 'send'">
            <Send class="mr-2 h-3.5 w-3.5" />
            Enviar mi primer feedback
          </Button>
        </div>

        <!-- Feedback list -->
        <div v-else class="space-y-3">
          <!-- Summary -->
          <div class="flex items-center gap-4 text-xs text-muted-foreground pb-2">
            <span>{{ myFeedbacks.length }} enviados</span>
            <span v-if="pendingCount > 0" class="text-primary font-medium">{{ pendingCount }} pendientes</span>
            <span v-if="withResponseCount > 0" class="text-emerald-500 font-medium">{{ withResponseCount }} con respuesta</span>
          </div>

          <div
            v-for="feedback in myFeedbacks"
            :key="feedback.id"
            class="border rounded-lg overflow-hidden transition-colors"
            :class="expandedFeedbackId === feedback.id ? 'border-primary/30 bg-primary/[0.02]' : ''"
          >
            <!-- Header (always visible) -->
            <button
              class="w-full flex items-start gap-3 p-3 text-left hover:bg-muted/50 transition-colors"
              @click="toggleExpand(feedback.id)"
            >
              <div
                class="h-8 w-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5"
                :class="typeConfig[feedback.type]?.color"
              >
                <component :is="typeConfig[feedback.type]?.icon" class="h-4 w-4" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 mb-0.5">
                  <p class="text-sm font-medium truncate">
                    {{ feedback.type === 'rating' ? `${renderStars(feedback.rating || 0)}` : feedback.subject }}
                  </p>
                </div>
                <div class="flex items-center gap-2">
                  <Badge
                    :variant="statusConfig[feedback.status]?.variant || 'outline'"
                    class="text-[10px] px-1.5 py-0"
                  >
                    {{ statusConfig[feedback.status]?.label }}
                  </Badge>
                  <span class="text-[11px] text-muted-foreground">{{ formatDate(feedback.createdAt) }}</span>
                  <div
                    v-if="feedback.adminResponse || (feedback.responses && feedback.responses.length > 0)"
                    class="flex items-center gap-1 text-[11px] text-emerald-500 font-medium"
                  >
                    <MessageCircle class="h-3 w-3" />
                    Respuesta
                  </div>
                </div>
              </div>
              <component
                :is="expandedFeedbackId === feedback.id ? ChevronUp : ChevronDown"
                class="h-4 w-4 text-muted-foreground shrink-0 mt-1"
              />
            </button>

            <!-- Expanded content -->
            <div v-if="expandedFeedbackId === feedback.id" class="px-3 pb-3 space-y-3">
              <Separator />

              <!-- Rating -->
              <div v-if="feedback.type === 'rating' && feedback.rating" class="flex items-center gap-2">
                <span class="text-lg tracking-wider text-yellow-500">{{ renderStars(feedback.rating) }}</span>
                <span class="text-xs text-muted-foreground">({{ feedback.rating }}/5)</span>
              </div>

              <!-- Description -->
              <div v-if="feedback.description && feedback.description !== 'Sin comentarios adicionales'">
                <p class="text-xs text-muted-foreground mb-1">Tu mensaje</p>
                <p class="text-sm whitespace-pre-wrap bg-muted/50 rounded-lg p-3">{{ feedback.description }}</p>
              </div>

              <!-- Page context -->
              <p v-if="feedback.currentPage" class="text-[11px] text-muted-foreground">
                Enviado desde: <span class="font-mono">{{ feedback.currentPage }}</span>
              </p>

              <!-- Admin Response (single) -->
              <div v-if="feedback.adminResponse" class="mt-2">
                <p class="text-xs text-muted-foreground mb-1.5 font-medium">Respuesta del equipo</p>
                <div class="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3">
                  <p class="text-sm whitespace-pre-wrap">{{ feedback.adminResponse }}</p>
                </div>
              </div>

              <!-- Response thread -->
              <div v-if="feedback.responses && feedback.responses.length > 0" class="space-y-2">
                <p class="text-xs text-muted-foreground font-medium">
                  Conversación ({{ feedback.responses.length }})
                </p>
                <div
                  v-for="response in feedback.responses"
                  :key="response.id"
                  class="rounded-lg border border-emerald-500/20 bg-emerald-500/5 p-3"
                >
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-xs font-medium text-emerald-600 dark:text-emerald-400">
                      {{ response.respondedByEmail }}
                    </span>
                    <span class="text-[10px] text-muted-foreground">
                      {{ formatDateTime(response.createdAt) }}
                    </span>
                  </div>
                  <p class="text-sm whitespace-pre-wrap">{{ response.message }}</p>
                </div>
              </div>

              <!-- No response yet -->
              <div
                v-if="!feedback.adminResponse && (!feedback.responses || feedback.responses.length === 0)"
                class="flex items-center gap-2 text-xs text-muted-foreground py-1"
              >
                <Clock class="h-3.5 w-3.5" />
                Pendiente de revisión por el equipo
              </div>
            </div>
          </div>
        </div>
      </TabsContent>
    </Tabs>
  </div>
</template>
